import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Building2, Star, Phone, Mail } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useVendors, useVendorCategories } from '../hooks/useVendors';
import { EntityList, StatusBadge } from '../components/common';
import type { EntityListColumn } from '../components/common';
import { EntityModal } from '../components/common/EntityModal';
import { CreateVendorPanel } from '../components/vendors';
import { RecordConversations } from '../components/messaging/RecordConversations';
import { useThreadCounts } from '../hooks/useThreadCounts';

const PAGE_SIZE = 25;

export function VendorsPage() {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [showCreate, setShowCreate] = useState(false);

  const { data: allVendors = [], isLoading } = useVendors();
  useVendorCategories(); // preload for detail pages

  const filtered = useMemo(() => {
    if (!search) return allVendors;
    const term = search.toLowerCase();
    return allVendors.filter(
      (v) =>
        v.businessName.toLowerCase().includes(term) ||
        v.contactName?.toLowerCase().includes(term) ||
        v.city?.toLowerCase().includes(term),
    );
  }, [allVendors, search]);

  const total = filtered.length;
  const items = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // One request for every row's badge, keyed on the visible page rather than
  // the whole filtered set — twenty-five badges should not cost a question
  // about every vendor in the system.
  const { countFor } = useThreadCounts(
    'Vendor',
    items.map((vendor) => vendor.id),
  );

  const columns: EntityListColumn<(typeof allVendors)[0]>[] = useMemo(
    () => [
      {
        key: 'name',
        header: 'Vendor',
        render: (vendor) => (
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <Building2 size={16} className="text-purple-600" />
            </div>
            <div>
              <p className="text-gray-900 font-semibold flex items-center gap-1.5">
                <span>{vendor.businessName}</span>
                <RecordConversations
                  entityType="Vendor"
                  entityId={vendor.id}
                  variant="icon"
                  count={countFor(vendor.id)}
                />
              </p>
              {vendor.contactName && <p className="text-xs text-gray-500">{vendor.contactName}</p>}
            </div>
          </div>
        ),
      },
      {
        key: 'contact',
        header: 'Contact',
        render: (vendor) => (
          <div className="text-xs text-gray-500 space-y-0.5">
            {vendor.phone && (
              <span className="flex items-center gap-1">
                <Phone size={10} /> {vendor.phone}
              </span>
            )}
            {vendor.email && (
              <span className="flex items-center gap-1">
                <Mail size={10} /> {vendor.email}
              </span>
            )}
          </div>
        ),
        hideOnMobile: true,
      },
      {
        key: 'rating',
        header: 'Rating',
        render: (vendor) =>
          vendor.rating != null ? (
            <span className="text-sm text-amber-500 flex items-center gap-0.5">
              <Star size={12} className="fill-amber-500" />
              {vendor.rating}
            </span>
          ) : (
            <span className="text-gray-400 text-sm">—</span>
          ),
        width: 'w-20',
        hideOnMobile: true,
      },
      {
        key: 'status',
        header: 'Status',
        render: (vendor) => (
          <StatusBadge
            label={vendor.isActive ? 'Active' : 'Inactive'}
            color={vendor.isActive ? 'green' : 'gray'}
          />
        ),
        width: 'w-24',
      },
    ],
    // Without this the memo holds the pre-fetch closure, which answers `null`
    // forever and renders no badge at all.
    [countFor],
  );

  if (!currentUser) return null;

  return (
    <div>
      <EntityList
        items={items}
        columns={columns}
        getKey={(vendor) => vendor.id}
        isLoading={isLoading}
        page={page}
        pageSize={PAGE_SIZE}
        total={total}
        onPageChange={setPage}
        searchValue={search}
        onSearchChange={(v) => {
          setSearch(v);
          setPage(1);
        }}
        searchPlaceholder="Search vendors..."
        onRowClick={(vendor) => navigate(`/vendors/${vendor.id}`)}
        onPreview={(vendor) => navigate(`/vendors/${vendor.id}`)}
        title="Vendors"
        titleActions={
          <button
            onClick={() => setShowCreate(true)}
            className="flex items-center gap-1.5 px-3 py-2 bg-primary-600 text-white text-sm rounded-lg hover:bg-primary-700 transition-colors"
          >
            <Plus size={16} />
            New Vendor
          </button>
        }
        emptyMessage="No vendors found"
      />

      <EntityModal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        title="New Vendor"
        size="md"
      >
        <CreateVendorPanel
          onClose={() => setShowCreate(false)}
          onCreated={() => setShowCreate(false)}
        />
      </EntityModal>
    </div>
  );
}
